import React from "react";
import { Button } from "@wac-ui-dashboard/wac_component_library";
import { useGetCustomerSubscriptionDetailsQuery } from "../../../../store/queries/Subscription";
import { getFormatedBusinessDate } from "../../../../utils/Functions/table";

const money = (value) =>
  value === undefined || value === null
    ? "-"
    : `KD ${Number(value).toFixed(3)}`;

// The same reading as the listing behind this dialog, so a date does not change shape on the
// way into it. Still a dash when there is nothing to show, which is this dialog's habit.
const day = (value) => getFormatedBusinessDate(value) || "-";

/** The ledger's own vocabulary, spelled out for whoever is reading it on the phone. */
const LEDGER_TYPES = {
  debit: "Redeemed",
  credit_void: "Credited (POS void)",
  credit_manual: "Credited by support",
  release_unpaid: "Released (unpaid order)",
  reclaim_released: "Re-taken (paid after release)",
  cancel_forfeit: "Written off (cancelled)",
};

/**
 * The running balance is derived rather than stored: walk the newest-first ledger backwards
 * from the live counter, so the top row always agrees with the "left of" figure above it.
 *
 * Opening from total_items and adding downwards would be the obvious way round. It is the
 * wrong one, because a ledger row can fail to write without failing the order it belongs to
 * - SubscriptionBalanceService::writeLedger logs and carries on, the balance having already
 * moved. Anchored at the counter, a row lost that way skews the old end of the column;
 * anchored at the sold quantity it would skew the recent end, which is the end being read.
 */
const withRunningBalance = (rows, remaining) => {
  let balance = Number(remaining) || 0;

  return rows.map((row) => {
    const entry = { ...row, balance_after: balance };
    balance -= Number(row?.quantity) || 0;

    return entry;
  });
};

const SubscriptionDetails = ({ subscriptionId, handleClose }) => {
  const { data: detailData = {}, isFetching } =
    useGetCustomerSubscriptionDetailsQuery(
      { subscription_id: subscriptionId },
      { skip: !subscriptionId }
    );

  if (isFetching) {
    return <p className="pro-mb-0">Loading…</p>;
  }

  const subscription = detailData?.data ?? {};
  const ledger = subscription?.ledger ?? [];
  const ledgerRows = withRunningBalance(ledger, subscription?.remaining_items);

  return (
    <div>
      <h5 className="pro-ttl pro-mb-1">{subscription?.plan_name}</h5>
      <p className="pro-font-sm pro-mb-4">
        {subscription?.subscription_code} · {subscription?.status_label}
        {subscription?.source ? ` · ${subscription.source}` : ""}
      </p>

      <div className="row pro-mb-4">
        <div className="col-6 pro-mb-2">
          <span className="pro-font-sm">Customer</span>
          <p className="pro-mb-0 pro-fw-medium">
            {subscription?.customer_name ?? "-"}
          </p>
        </div>
        <div className="col-6 pro-mb-2">
          <span className="pro-font-sm">Mobile</span>
          <p className="pro-mb-0 pro-fw-medium">
            {subscription?.customer_mobile ?? "-"}
          </p>
        </div>
        <div className="col-6 pro-mb-2">
          <span className="pro-font-sm">Items</span>
          <p className="pro-mb-0 pro-fw-medium">
            {subscription?.remaining_items} left of {subscription?.total_items} ·{" "}
            {subscription?.redeemed_items} redeemed
          </p>
        </div>
        <div className="col-6 pro-mb-2">
          <span className="pro-font-sm">Outstanding value</span>
          <p className="pro-mb-0 pro-fw-medium">
            {money(subscription?.outstanding_value)}
          </p>
        </div>
        <div className="col-6 pro-mb-2">
          <span className="pro-font-sm">Activated</span>
          <p className="pro-mb-0 pro-fw-medium">
            {day(subscription?.activated_at)}
          </p>
        </div>
        <div className="col-6 pro-mb-2">
          <span className="pro-font-sm">Expires</span>
          <p className="pro-mb-0 pro-fw-medium">
            {day(subscription?.expires_at)}
            {subscription?.days_until_expiry !== null &&
            subscription?.days_until_expiry !== undefined
              ? ` (${subscription.days_until_expiry} days)`
              : ""}
          </p>
        </div>
        <div className="col-6 pro-mb-2">
          <span className="pro-font-sm">Selling price</span>
          <p className="pro-mb-0 pro-fw-medium">
            {money(subscription?.selling_price)}
          </p>
        </div>
        <div className="col-6 pro-mb-2">
          {/* What this holder was charged - stays KD 0.000 for a gift recipient or a
              corporate redemption, which is why the selling price is its own field. */}
          <span className="pro-font-sm">Paid</span>
          <p className="pro-mb-0 pro-fw-medium">
            {money(subscription?.amount_paid)}
            {subscription?.payment_mode ? ` · ${subscription.payment_mode}` : ""}
          </p>
        </div>
        {subscription?.corporate_code && (
          <div className="col-6 pro-mb-2">
            <span className="pro-font-sm">Corporate code</span>
            <p className="pro-mb-0 pro-fw-medium">
              {subscription.corporate_code}
            </p>
          </div>
        )}
        {subscription?.cancelled_at && (
          <div className="col-12 pro-mb-2">
            <span className="pro-font-sm">Cancelled</span>
            <p className="pro-mb-0 pro-fw-medium">
              {day(subscription?.cancelled_at)} ·{" "}
              {money(subscription?.refund_amount)} refunded to{" "}
              {subscription?.refund_mode ?? "nothing"} ·{" "}
              {subscription?.cancellation_reason}
            </p>
          </div>
        )}
      </div>

      <h6 className="pro-ttl pro-mb-2">Ledger ({ledger.length})</h6>
      {ledger.length === 0 ? (
        <p className="pro-font-sm pro-mb-0">Nothing has moved yet.</p>
      ) : (
        <div className="pro-table-wrapper pro-overflow-auto">
          <table className="pro-table">
            <thead>
              <tr>
                <th>When</th>
                <th>What</th>
                <th>Item</th>
                <th>Qty</th>
                <th>Balance</th>
                <th>Discount given</th>
                <th>Order</th>
                <th>By</th>
              </tr>
            </thead>
            <tbody>
              {ledgerRows.map((row) => (
                <tr key={row?.ledger_id}>
                  <td>{day(row?.created_at)}</td>
                  <td>{LEDGER_TYPES[row?.type] ?? row?.type}</td>
                  <td>{row?.item_name}</td>
                  {/* Signed: negative when items leave the balance, positive when they return. */}
                  <td>{row?.quantity}</td>
                  {/* What was left once this row had moved, across every line the plan carries. */}
                  <td>{row?.balance_after}</td>
                  <td>{money(row?.gross_discount_value)}</td>
                  <td>{row?.order_id ?? "-"}</td>
                  <td>{row?.admin_id ? `admin · ${row?.reason ?? ""}` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="pro-d-flex pro-justify-end pro-mt-4">
        <Button className={"pro-btn-link lg"} onClick={handleClose}>
          Close
        </Button>
      </div>
    </div>
  );
};

export default SubscriptionDetails;
